import markConversationsAsReadFromTable from './markConversationsAsReadFromTable';
import markItemsAsReadFromTable from './markItemsAsReadFromTable';
import { logUsage } from 'owa-analytics';
import { ActionSource } from 'owa-analytics-actions';
import { confirm, ConfirmCustomizationOptions, DialogResponse } from 'owa-confirm-dialog';
import { asDate } from 'owa-datetime';
import folderStore, { getEffectiveFolderDisplayName } from 'owa-folders';
import { lazyAddMarkAsReadDiagnostics } from 'owa-group-readunread-diagnostics';
import loc, { format } from 'owa-localize';
import { markAllAsRead } from 'owa-locstrings/lib/strings/markallasread.locstring.json';
import { markAsRead } from 'owa-locstrings/lib/strings/markasread.locstring.json';
import { markAsUnread } from 'owa-locstrings/lib/strings/markasunread.locstring.json';
import { okButton } from 'owa-locstrings/lib/strings/okbutton.locstring.json';
import markAllAsReadStoreUpdate from 'owa-mail-actions/lib/triage/markAllAsReadStoreUpdate';
import { TableView } from 'owa-mail-list-store';
import { getFolderScopeText } from 'owa-mail-list-store';
import getItemIdsFromConversations from 'owa-mail-list-store/lib/utils/getItemIdsFromConversations';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import { markAllAsReadService } from 'owa-mail-store/lib/services/markAllAsReadService';
import { NUMBER_OF_SELECTED_ITEMS_THRESHOLD } from 'owa-mail-store/lib/utils/bulkTriageConstants';
import isSharedFolder from 'owa-mail-store/lib/utils/isSharedFolder';
import * as undoActions from 'owa-mail-undo';
import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { action } from 'satcheljs/lib/legacy';
import {
    markAsReadFolderConfirmSubtext,
    markAsUnreadFolderConfirmSubtext,
} from './markAsReadInTable.locstring.json';
import {
    getBaseServerFolderId,
    getFocusedFilterForTable,
    listViewStore,
    isFolderPaused,
    getInboxClassificationFilterForTable,
    getRowIdsFromRowKeys,
} from 'owa-mail-list-store';

/**
 * REMARK: exclusionList and isInVirtualSelectMode (which is generally what's
 * used for isActingOnAllItemsInTable) is not read from tableView due to specific
 * special-cases, primarily "Mark all as read" command or when we're in search
 * results where there is no backing folder.
 */
export default action('markAsReadInTable')(function markAsReadInTable(
    actionSource: ActionSource,
    exclusionList: string[],
    isActingOnAllItemsInTable: boolean,
    isReadValueToSet: boolean,
    rowKeysToActOn: string[],
    tableView: TableView,
    isMarkAllAsRead?: boolean
) {
    if (rowKeysToActOn.length && isActingOnAllItemsInTable) {
        throw new Error('If in virtual select all mode, rowKeysToActOn should be empty.');
    }

    if (exclusionList.length && !isActingOnAllItemsInTable) {
        throw new Error('If not in virtual select all mode, exclusionList should be empty.');
    }

    const folderId = tableView.tableQuery.folderId;
    const folder = folderStore.folderTable.get(folderId);

    // Get display name from folder (or fall back if search results).
    const displayName = getFolderScopeText(
        getFocusedFilterForTable(tableView),
        getInboxClassificationFilterForTable(tableView.id),
        tableView.tableQuery.type,
        folder && getEffectiveFolderDisplayName(folder)
    );

    // Determine strings for confirmation dialog.
    const markAsReadText = isMarkAllAsRead ? loc(markAllAsRead) : loc(markAsRead);
    const headerText = isReadValueToSet ? markAsReadText : loc(markAsUnread);
    const subText = isReadValueToSet
        ? format(loc(markAsReadFolderConfirmSubtext), displayName)
        : format(loc(markAsUnreadFolderConfirmSubtext), displayName);

    /**
     * Calculate whether to show confirmation dialog in virtual select all mode when the
     * number of unread or read items exceeds threshold (currently 25 items).
     **/
    const shouldShowConfirmDialogWhenActingOnAllItems =
        isActingOnAllItemsInTable &&
        folder &&
        (folder.UnreadCount >= NUMBER_OF_SELECTED_ITEMS_THRESHOLD ||
            folder.TotalCount - folder.UnreadCount >= NUMBER_OF_SELECTED_ITEMS_THRESHOLD);

    // Show confirmation dialog when either above condition is true in virtual select mode or
    // we have more than the threshold number of rows to act on.
    const showConfirmationDialog =
        shouldShowConfirmDialogWhenActingOnAllItems ||
        rowKeysToActOn.length >= NUMBER_OF_SELECTED_ITEMS_THRESHOLD;

    confirm(headerText, subText, !showConfirmationDialog /* resolveImmediately */, <
        ConfirmCustomizationOptions
    >{
        // Wait until dialog is dismissed to avoid focus interruption.
        delayCallbackAfterAnimation: true,
        okText: loc(okButton),
    }).then(async (response: DialogResponse) => {
        if (response === DialogResponse.ok) {
            undoActions.clearLastUndoableAction();

            if (isActingOnAllItemsInTable) {
                const readReceiptConfiguration = getUserConfiguration().UserOptions.ReadReceipt;
                const suppressReadReceipts =
                    readReceiptConfiguration == 'DoNotAutomaticallySend' ||
                    readReceiptConfiguration == 'NeverSend' ||
                    isSharedFolder(folderId);

                // When folder is paused we shall act only on items that were delivered before the pause time
                const clientLastSyncTime = isFolderPaused(folderId)
                    ? asDate(listViewStore.inboxPausedDateTime)
                    : null;

                logMarkAllAsReadUnreadDatapoint(actionSource, isReadValueToSet, tableView);

                // Tracking the mark as read operation for the diagnostics panel
                lazyAddMarkAsReadDiagnostics.import().then(addMarkAsReadDiagnostics => {
                    addMarkAsReadDiagnostics(
                        tableView.tableQuery,
                        true /* isActingOnAllItemsInTable */,
                        isReadValueToSet,
                        actionSource
                    );
                });

                let exclusionItemIds = [];
                // Convert RowKeys to ItemIds depending on whether it is a conversation or items view
                switch (tableView.tableQuery.listViewType) {
                    case ReactListViewType.Conversation:
                        exclusionItemIds = getItemIdsFromConversations(exclusionList, tableView.id);
                        break;
                    case ReactListViewType.Message:
                        exclusionItemIds = getRowIdsFromRowKeys(exclusionList, tableView.id);
                        break;
                }

                markAllAsReadStoreUpdate(tableView, isReadValueToSet, exclusionItemIds);

                markAllAsReadService(
                    getBaseServerFolderId(tableView),
                    isReadValueToSet,
                    exclusionItemIds,
                    suppressReadReceipts,
                    getMailboxInfo(tableView).requestOptions,
                    clientLastSyncTime
                );
            } else {
                switch (tableView.tableQuery.listViewType) {
                    case ReactListViewType.Conversation:
                        markConversationsAsReadFromTable(
                            rowKeysToActOn,
                            tableView,
                            isReadValueToSet,
                            actionSource
                        );
                        break;

                    case ReactListViewType.Message:
                        markItemsAsReadFromTable(
                            rowKeysToActOn,
                            tableView,
                            isReadValueToSet,
                            true /* isExplicit */,
                            actionSource
                        );
                        break;
                }
            }
        }
    });
});

function logMarkAllAsReadUnreadDatapoint(
    actionSource: ActionSource,
    isReadValueToSet: boolean,
    tableView: TableView
) {
    let name = 'TnS_MarkAllAs';
    name += isReadValueToSet ? 'Read' : 'Unread';
    logUsage(name, [actionSource, getFocusedFilterForTable(tableView), tableView.isInCheckedMode]);
}
